import axios from "axios";
import moment from "moment";
import { dbService } from "../firebaseInstance";
import { arrayUnion } from "firebase/firestore";
import i18n from '../i18n/config';
import { uploadAndMakePublicAvatar } from './publicAvatarUtils';
import { 
  calculateDocumentSize
} from './firestoreShardingUtils';
import { processThreadMediaWithProxy } from './mediaDownloader';
import { getActiveToken, handleTokenError } from './getActiveToken';
import { graphGet } from './graphProxy';
import { updateThreadsToFirestore } from './threadDateRangeUpdate';
import { errorLogger } from './errorLogger';
import { 
  formatDateRange as formatProgressDateRange, 
  calculateSize, 
  filterThreadsForStorage,
  calculateProgress,
  shouldSaveChunk
} from './progressiveLoadingUtils';

// === Collection Management Functions ===

/**
 * Pure 함수: 사용자의 컬렉션 목록 가져오기
 * @param {string} userID - 사용자 ID
 * @returns {Promise<Array>} 컬렉션 목록
 */
export const getMyCollections = async (userID) => {
	console.log("[getMyCollections] START - userID:", userID);
	try {
		// Firebase 초기화 체크
		if (!dbService) {
			throw new Error("Firebase dbService is not initialized");
		}

		// 🔥 NEW: collection 컬렉션에서 userID로 직접 쿼리
		// collectionList 배열 동기화 문제를 우회하여 모든 컬렉션을 가져옴
		console.log("[getMyCollections] Querying collection with userID...");
		const collectionSnapshot = await dbService
			.collection("collection")
			.where("userID", "==", userID)
			.get();

		console.log(`[getMyCollections] Found ${collectionSnapshot.size} collections`);

		if (collectionSnapshot.empty) {
			console.log("[getMyCollections] No collections found for user");
			return [];
		}

		const collections = collectionSnapshot.docs.map((doc) => {
			const collectionData = doc.data();
			const threadList = collectionData.threadList || [];
			console.log(`[getMyCollections] Collection ${doc.id} has ${threadList.length} threads`);

			// 메트릭 계산
			const metric = {
				replies: 0,
				reposts: 0,
				likes: 0,
				views: 0,
			};

			threadList.forEach((thread) => {
				metric.replies += thread.replies || 0;
				metric.reposts += thread.reposts || 0;
				metric.likes += thread.likes || 0;
				metric.views += thread.views || 0;
			});

			collectionData.metric = metric;

			return { id: doc.id, ...collectionData };
		});

		console.log(`[getMyCollections] Successfully loaded ${collections.length} collections`);
		return collections;
	} catch (error) {
		console.error("Error fetching collections: ", error);
		throw error;
	}
};

/**
 * Pure 함수: 새 컬렉션 생성
 * @param {Object} data - 컬렉션 데이터
 * @returns {Promise<Object>} 생성된 컬렉션 정보
 */
export const createCollection = async (data) => {
	try {
		// lastUpdated 필드 추가
		const collectionData = {
			...data,
			lastUpdated: Math.floor(Date.now() / 1000) // ✅ Unix timestamp로 통일
		};

    // Enrich threadList with latest original thread (including text_attachment)
    const enrichedList = await ensureThreadsEnrichedForStorage(collectionData.threadList || []);
    const finalData = deepRemoveUndefinedLocal({
      ...collectionData,
      threadList: enrichedList
    });

    const collectionRef = dbService.collection("collection");
    const docRef = await collectionRef.add(finalData);

		const userRef = dbService.collection("user").doc(data.userID);
		await userRef.update({
			collectionList: arrayUnion(docRef.id),
		});

    return { id: docRef.id, ...finalData };
	} catch (error) {
		console.error("Error adding collection: ", error);
		throw error;
	}
};

/**
 * 스레드의 미디어 URL을 실시간으로 새로고침
 * @param {string} threadId - 스레드 ID
 * @returns {Promise<Object>} 새로운 미디어 정보
 */
const refreshThreadMediaUrl = async (threadId) => {
	const token = getActiveToken();
	if (!token) return null;
	
	try {
    const response = await graphGet(
			`${threadId}`,
			{
        params: {
          fields: "id,media_product_type,media_type,media_url,permalink,owner,username,text,topic_tag,timestamp,shortcode,thumbnail_url,children{media_type,media_url,alt_text},is_quote_post,quoted_post,reposted_post,alt_text,link_attachment_url,text_attachment,is_spoiler_media,text_entities,gif_url,poll_attachment",
					access_token: token,
				},
			}
		);
    // RAW: single thread fetch including text_attachment
    try { console.log('[RAW] single-thread response:', response.data); } catch (_) {}
		
		return response.data;
	} catch (error) {
		console.warn(`Failed to refresh media URL for thread ${threadId}:`, error);
		return null;
	}
};

// ===== Collection Enrichment Helpers =====
// Normalize Threads API text_attachment into plain string for storage/rendering
const normalizeTextAttachment = (att) => {
	if (!att) return '';
	if (typeof att === 'string') return att;
	if (Array.isArray(att)) {
 		return att.map(normalizeTextAttachment).filter(Boolean).join('\n');
 	}
 	if (typeof att === 'object') {
 		return (
 			att.plaintext || att.plain_text || att.text ||
 			(typeof att.content === 'string' ? att.content : '') || ''
 		);
 	}
 	return String(att);
};

// Remove undefined recursively to avoid Firestore undefined errors
const deepRemoveUndefinedLocal = (value) => {
 	if (Array.isArray(value)) {
 		return value
 			.map((item) => deepRemoveUndefinedLocal(item))
 			.filter((v) => v !== undefined);
 	}
 	if (value && typeof value === 'object') {
 		const next = {};
 		Object.keys(value).forEach((key) => {
 			const v = deepRemoveUndefinedLocal(value[key]);
 			if (v !== undefined) next[key] = v;
 		});
 		return next;
 	}
 	return value === undefined ? undefined : value;
};

// Project the API thread payload + fallback thread into minimal storage shape
const projectThreadForStorage = (apiThread, fallback) => {
 	const normalizedTextAttachment = normalizeTextAttachment(apiThread?.text_attachment);
 	const hasChildren = apiThread?.children && apiThread.children.data && Array.isArray(apiThread.children.data);
 	const storage = {
 		id: fallback?.id || apiThread?.id,
 		text: apiThread?.text ?? fallback?.text ?? '',
 		text_attachment: normalizedTextAttachment || undefined,
 		link_attachment_url: apiThread?.link_attachment_url || undefined,
 		timestamp: apiThread?.timestamp ?? fallback?.timestamp,
 		username: apiThread?.username ?? fallback?.username,
 		permalink: apiThread?.permalink ?? fallback?.permalink,
 		// keep metrics if present in fallback
 		views: fallback?.views,
 		likes: fallback?.likes,
 		replies: fallback?.replies,
 		reposts: fallback?.reposts,
 	};
 	// Media subset (optional)
 	if (apiThread?.media_url || hasChildren) {
 		storage.media_type = apiThread.media_type;
 		storage.media_url = apiThread.media_url;
 		storage.alt_text = apiThread.alt_text;
 		storage.children = apiThread.children;
 	}
 	return deepRemoveUndefinedLocal(storage);
};

// Enrich an array of threads by fetching each original from Threads API
const ensureThreadsEnrichedForStorage = async (threadList) => {
 	if (!Array.isArray(threadList) || threadList.length === 0) return [];
 	const enriched = await Promise.all(
 		threadList.map(async (t) => {
 			try {
 				const apiThread = await refreshThreadMediaUrl(t.id);
 				return projectThreadForStorage(apiThread || {}, t);
 			} catch (e) {
 				console.warn('Failed to enrich thread for storage:', t?.id, e?.message);
 				return projectThreadForStorage({}, t);
 			}
 		})
 	);
 	return enriched;
};

/**
 * Admin: Backfill text_attachment for a collection's threads
 * @param {string} collectionId
 * @param {{ threadIds?: string[], dryRun?: boolean }} options
 * @returns {Promise<{collectionId: string, processed: number, updated: number, diffs: Array}>}
 */
export const backfillCollectionTextAttachments = async (collectionId, options = {}) => {
  const { threadIds = null, dryRun = false } = options;
  if (!collectionId) throw new Error('collectionId is required');

  const collectionRef = dbService.collection('collection').doc(collectionId);
  const doc = await collectionRef.get();
  if (!doc.exists) throw new Error(`Collection with ID ${collectionId} not found`);

  const data = doc.data() || {};
  const originalList = Array.isArray(data.threadList) ? data.threadList : [];
  const idAllow = threadIds && threadIds.length > 0 ? new Set(threadIds) : null;
  const targetList = originalList.filter(t => (idAllow ? idAllow.has(t.id) : true));

  const enrichedSubset = await ensureThreadsEnrichedForStorage(targetList);
  const enrichedMap = new Map(enrichedSubset.map(t => [t.id, t]));

  // Build diffs and merged list
  const diffs = [];
  let updated = 0;
  const merged = originalList.map(oldItem => {
    const fresh = enrichedMap.get(oldItem.id);
    if (!fresh) return oldItem;
    const beforeTA = oldItem.text_attachment || '';
    const afterTA = fresh.text_attachment || '';
    const beforeLink = oldItem.link_attachment_url || '';
    const afterLink = fresh.link_attachment_url || '';
    const changed = (beforeTA !== afterTA) || (beforeLink !== afterLink) ||
      (oldItem.text !== (fresh.text || oldItem.text));
    if (changed) {
      updated += 1;
      diffs.push({ id: oldItem.id, beforeTA, afterTA, beforeLink, afterLink });
    }
    return { ...oldItem, ...fresh };
  });

  if (dryRun) {
    return { collectionId, processed: targetList.length, updated, diffs };
  }

  const lastUpdated = Math.floor(Date.now() / 1000);
  const payload = deepRemoveUndefinedLocal({ threadList: merged, lastUpdated });
  await collectionRef.update(payload);
  return { collectionId, processed: targetList.length, updated, diffs };
};

/**
 * Pure 함수: 단일 컬렉션 정보 가져오기 (미디어 URL 새로고침 포함)
 * @param {string} collectionId - 컬렉션 ID
 * @returns {Promise<Object>} 컬렉션 데이터
 */
export const getCollection = async (collectionId) => {
	try {
		const collectionRef = dbService.collection("collection").doc(collectionId);
		const collectionDoc = await collectionRef.get();
		
		if (!collectionDoc.exists) {
			throw new Error(`Collection with ID ${collectionId} not found`);
		}
		
		const collection = { id: collectionId, ...collectionDoc.data() };
		
		// 🔥 미디어가 있는 스레드들의 URL을 실시간으로 새로고침
		if (collection.threadList && collection.threadList.length > 0) {
			console.log("🔄 Refreshing media URLs for collection threads...");
			
      const refreshedThreadList = await Promise.all(
        collection.threadList.map(async (thread) => {
          // 항상 원문을 Graph API에서 최신으로 로드 (text_attachment 포함 여부 확인)
          console.log(`🔄 Fetching original thread by ID: ${thread.id}`);
          const fresh = await refreshThreadMediaUrl(thread.id);
          // 전체 payload 로그는 refreshThreadMediaUrl 내부에서 수행
          if (!fresh) return thread;
          return {
            ...thread,
            // 원문 최신 값으로 동기화
            text: fresh.text ?? thread.text,
            media_url: fresh.media_url ?? thread.media_url,
            media_type: fresh.media_type ?? thread.media_type,
            children: fresh.children ?? thread.children,
            alt_text: fresh.alt_text ?? thread.alt_text,
            permalink: fresh.permalink ?? thread.permalink,
            username: fresh.username ?? thread.username,
            // 첨부 계열
            text_attachment: fresh.text_attachment,
            link_attachment_url: fresh.link_attachment_url,
            // 메타
            mediaRefreshed: true,
            mediaRefreshedAt: new Date().toISOString()
          };
        })
      );
			
			const refreshedCount = refreshedThreadList.filter(t => t.mediaRefreshed).length;
			console.log(`✅ Media URL refresh complete: ${refreshedCount}/${collection.threadList.length} threads refreshed`);
			
			return {
				...collection,
				threadList: refreshedThreadList
			};
		}
		
		return collection;
	} catch (error) {
		console.error("Error fetching collection: ", error);
		throw error;
	}
};

/**
 * Pure 함수: 컬렉션 업데이트
 * @param {string} collectionID - 컬렉션 ID
 * @param {Object} data - 업데이트할 데이터
 * @returns {Promise<Object>} 업데이트된 컬렉션 정보
 */
export const updateCollection = async (collectionID, data) => {
	try { 
		// lastUpdated 필드 자동 업데이트
    const enrichedList = await ensureThreadsEnrichedForStorage(data.threadList || []);
    const updateData = deepRemoveUndefinedLocal({
      ...data,
      threadList: enrichedList,
      lastUpdated: Math.floor(Date.now() / 1000) // ✅ Unix timestamp로 통일
    });

		const collectionRef = dbService.collection("collection").doc(collectionID);
		await collectionRef.update(updateData);

		return { id: collectionID, ...updateData };
	} catch (error) {
		console.error("Error updating collection: ", error);
		throw error;
	}
};

/**
 * Pure 함수: 컬렉션 삭제
 * @param {string} collectionID - 컬렉션 ID
 * @param {string} userID - 사용자 ID (선택적)
 * @returns {Promise<void>}
 */
export const deleteCollection = async (collectionID, userID = null) => {
	try {
		// 컬렉션 문서 삭제
		const collectionRef = dbService.collection("collection").doc(collectionID);
		await collectionRef.delete();
		
		// 사용자의 collectionList에서 제거
		if (userID) {
			const userRef = dbService.collection("user").doc(userID);
			const userDoc = await userRef.get();
			
			if (userDoc.exists) {
				const userData = userDoc.data();
				const updatedCollectionList = (userData.collectionList || []).filter(
					id => id !== collectionID
				);
				
				await userRef.update({
					collectionList: updatedCollectionList
				});
			}
		}
	} catch (error) {
		console.error("Error deleting collection: ", error);
		throw error;
	}
};

/**
 * Pure 함수: 모든 컬렉션 가져오기
 * @returns {Promise<Array>} 모든 컬렉션 목록
 */
export const getAllCollection = async () => {
	try {
		const collectionRef = dbService.collection("collection");
		const snapshot = await collectionRef.get();
		const collections = snapshot.docs.map((doc) => ({
			id: doc.id,
			...doc.data(),
		}));
		return collections;
	} catch (error) {
		console.error("Error fetching all collections: ", error);
		throw error;
	}
};

/**
 * Pure 함수: 사용자 인사이트 데이터 가져오기
 * @param {string} userID - 사용자 ID
 * @returns {Promise<Object>} 사용자 데이터
 */
export const getUserInsight = async (userID) => {
	try {
		const userRef = dbService.collection("user").doc(userID);
		const userDoc = await userRef.get();
		
		if (!userDoc.exists) {
			throw new Error(`User with ID ${userID} not found`);
		}
		
		return userDoc.data();
	} catch (error) {
		console.error("Error fetching user insight: ", error);
		throw error;
	}
};

/**
 * Pure 함수: 모든 사용자 정보 가져오기 (ThreaderList용)
 * @returns {Promise<Array>} 모든 사용자 목록
 */
export const getAllUsers = async () => {
	try {
		const userDocRef = dbService.collection("user");
		const userDoc = await userDocRef.get();
		const usersData = [];

		for (const doc of userDoc.docs) {
			const userData = doc.data();
			const userInsightDoc = await doc.ref
				.collection("insight")
				.doc("insight")
				.get();
			const userInsight = userInsightDoc.exists ? userInsightDoc.data() : {};
			
			// 프로필이 있는 유저만 포함
			if (userData.profile) {
				usersData.push({ 
					id: doc.id,
					...userData, 
					insight: userInsight 
				});
			}
		}

		return usersData;
	} catch (error) {
		console.error("Error fetching all users: ", error);
		throw error;
	}
};

// Graph API 프록시 헬퍼는 공용 모듈로 분리됨 (src/utils/graphProxy.js)
// graph.threads.net 의 데이터 엔드포인트는 브라우저 CORS 미지원 → threadsApiProxy CF 경유

// === 사용자 프로필 관련 함수 ===

/**
 * Pure 함수: 사용자 프로필 정보 가져오기
 * @param {string} token - 액세스 토큰
 * @returns {Promise<Object>} 프로필 데이터
 */
export const getProfilePure = async (token) => {
	if (!token) {
		throw new Error("No access token provided");
	}
	
	try {
		const response = await graphGet('me', {
			params: {
				fields: "id,username,name,threads_profile_picture_url,threads_biography",
				access_token: token,
			},
		}, { retryOn5xx: false, recoverAuth: false });
		return response.data;
	} catch (error) {
		// 토큰 관련 에러인 경우 localStorage 정리 (갱신 시도 없이)
		handleTokenError(error);
		throw error;
	}
};

/**
 * Pure 함수: 사용자 로그인 시 프로필 및 아바타 처리
 * @param {string} token - 액세스 토큰
 * @returns {Promise<Object>} 처리된 프로필 데이터
 */
export const processUserLoginPure = async (token) => {
	try {
		// 1. 프로필 정보 가져오기
		const profileData = await getProfilePure(token);
		
		// 2. 아바타 업로드 및 Public 설정 (백그라운드 실행, 선택적)
		if (profileData.id && profileData.threads_profile_picture_url) {
			console.log('🔄 Starting avatar upload for user:', profileData.id);
			
			// 백그라운드에서 아바타 업로드 실행 (실패해도 로그인 진행)
			// 권한 오류 시 자동으로 건너뛰기
			uploadAndMakePublicAvatar(profileData.id, profileData.threads_profile_picture_url)
				.then(publicUrl => {
					console.log('✅ Avatar uploaded successfully:', publicUrl);
				})
				.catch(error => {
					console.warn('⚠️ Avatar upload failed (non-critical):', error.message);
					// 권한 오류인 경우 추가 정보 제공
					if (error.code === 'storage/unauthorized') {
						console.info('💡 Firebase Storage 권한이 필요합니다. 아바타 동기화 버튼을 사용해주세요.');
					}
				});
		}
		
		return profileData;
		
	} catch (error) {
		console.error("Error processing user login:", error);
		throw error;
	}
};

// === 에러 처리 및 재시도 로직 ===

/**
 * Exponential Backoff를 사용한 재시도 함수
 */
const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
	for (let attempt = 0; attempt < maxRetries; attempt++) {
		try {
			return await fn();
		} catch (error) {
			const isLastAttempt = attempt === maxRetries - 1;
			
			// 마지막 시도거나 재시도 불가능한 에러면 바로 throw
			if (isLastAttempt || error.response?.status === 401 || error.response?.status === 403) {
				throw error;
			}
			
			// 네트워크 에러나 5xx 에러만 재시도
			const shouldRetry = !error.response || error.response.status >= 500 || error.code === 'NETWORK_ERROR';
			if (!shouldRetry) {
				throw error;
			}
			
			const delay = baseDelay * Math.pow(2, attempt);
			await new Promise(resolve => setTimeout(resolve, delay));
		}
	}
};

/**
 * Pure 함수: 개별 스레드 인사이트 가져오기 (재시도 로직 포함)
 * @param {string} threadId - 스레드 ID
 */
export const getThreadInsightsPure = async (threadId) => {
	const token = getActiveToken();
	
	try {
		return await retryWithBackoff(async () => {
			const response = await graphGet(
				`${threadId}/insights`,
				{
					params: {
						metric: "views, likes,replies,reposts",
						access_token: token,
					},
				},
				{ retryOn5xx: false }
			);
			return response.data.data.reduce((acc, item) => {
				acc[item.name] = item.values[0].value;
				return acc;
			}, {});
		});
	} catch (error) {
		console.error(`Error fetching insights for thread ${threadId}:`, error);
		return {};
	}
};

/**
 * 네트워크 상태 기반 최적 배치 설정 계산
 */
const getOptimalBatchSettings = () => {
	try {
		const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
		
		if (connection) {
			// 네트워크 타입에 따른 최적화
			switch (connection.effectiveType) {
				case '4g':
					return { chunkSize: 100, delay: 50 };
				case '3g':
					return { chunkSize: 50, delay: 100 };
				case '2g':
					return { chunkSize: 25, delay: 200 };
				default:
					return { chunkSize: 50, delay: 100 };
			}
		}
		
		// 기본값 (네트워크 정보 없을 때)
		return { chunkSize: 50, delay: 100 };
	} catch (error) {
		console.warn('Failed to detect network condition:', error);
		return { chunkSize: 50, delay: 100 };
	}
};

/**
 * 배치 스레드 인사이트 로딩 (최적화된 청크 단위)
 * @param {Array} threads - 스레드 배열
 * @param {number} customChunkSize - 커스텀 청크 크기 (선택적)
 * @param {Function} onProgress - 진행률 콜백 (선택적)
 */
export const getBatchThreadInsights = async (threads, customChunkSize = null, onProgress = null) => {
	if (!threads || threads.length === 0) return [];
	
	const batchSettings = getOptimalBatchSettings();
	const chunkSize = customChunkSize || batchSettings.chunkSize;
	const delay = batchSettings.delay;
	
	if (onProgress) {
		onProgress({
			stage: 'batch-insights',
			message: i18n.t('loading.analyzing_threads'),
			total: Math.ceil(threads.length / chunkSize),
			completed: 0
		});
	}
	
	const results = [];
	
	for (let i = 0; i < threads.length; i += chunkSize) {
		const chunk = threads.slice(i, i + chunkSize);
		const batchNumber = Math.floor(i / chunkSize) + 1;
		const totalBatches = Math.ceil(threads.length / chunkSize);
		
		// 진행률 업데이트
		if (onProgress) {
			onProgress({
				stage: 'batch-insights',
				message: i18n.t('loading.analyzing_threads'),
				subMessage: i18n.t('loading.batch_processing', { current: batchNumber, total: totalBatches }),
				total: totalBatches,
				completed: batchNumber - 1
			});
		}
		
		try {
			// 청크 내에서 병렬 처리
			const chunkResults = await Promise.all(
				chunk.map(async (thread) => {
					if (thread.media_type !== "REPOST_FACADE") {
						const insights = await getThreadInsightsPure(thread.id);
						return { ...thread, ...insights };
					} else {
						return { ...thread, views: 0, likes: 0, replies: 0, reposts: 0 };
					}
				})
			);
			
			results.push(...chunkResults);
			
			// 최적화된 지연 시간 적용
			if (i + chunkSize < threads.length) {
				await new Promise(resolve => setTimeout(resolve, delay));
			}
		} catch (error) {
			console.error(`❌ Error processing batch ${batchNumber}:`, error);
			// 에러 시 인사이트 없이 원본 스레드 추가
			results.push(...chunk.map(thread => ({ 
				...thread, 
				views: 0, likes: 0, replies: 0, reposts: 0 
			})));
		}
	}
	
	return results;
};

/**
 * Pure 함수: 사용자 인사이트 가져오기 (재시도 로직 포함)
 * @param {number} since - Unix timestamp
 * @param {number} until - Unix timestamp
 */
export const getInsightPure = async (since, until) => {
	const token = getActiveToken();
	if (!token) {
		throw new Error("No access token available for insights");
	}
	const userID = localStorage.getItem("userID");
	const insightPath = `${userID}/threads_insights`;

	try {
		return await retryWithBackoff(async () => {
			return await graphGet(insightPath, {
				params: {
					since: since,
					until: until,
					metric: "views, followers_count, likes, replies, reposts",
					access_token: token,
				},
			}, { retryOn5xx: true });
		});
	} catch (error) {
		console.error("Error fetching insights:", error);
		throw error;
	}
};

/**
 * Pure 함수: 스레드 댓글 가져오기 (재시도 로직 포함)
 * @param {string} threadId - 스레드 ID
 */
export const getRepliesPure = async (threadId) => {
	const token = getActiveToken();

	try {
		return await retryWithBackoff(async () => {
			const response = await graphGet(`${threadId}/replies`, {
				params: {
					fields: "id,text,username,user_id",
					reverse: false,
					access_token: token,
				},
			}, { retryOn5xx: false });
			return response.data.data;
		});
	} catch (error) {
		console.error(`Error fetching replies for thread ${threadId}:`, error);
		return []; // 댓글 로딩 실패 시 빈 배열 반환
	}
};

/**
 * 대화(conversation) 프록시 Cloud Function URL
 * graph.threads.net 의 /conversation 엔드포인트는 브라우저 CORS 응답 헤더를 내려주지 않아
 * 직접 호출이 차단된다. 서버(fetchConversations)를 경유해 우회한다.
 */
const FETCH_CONVERSATIONS_URL =
	"https://us-central1-my-threads-insight.cloudfunctions.net/fetchConversations";

const CONVERSATION_FIELDS =
	"id,text,username,user_id,timestamp,replied_to,is_reply,is_reply_owned_by_me,root_post,permalink,media_type,media_url,alt_text,children{media_type,media_url,alt_text},text_attachment,link_attachment_url";

/**
 * Pure 함수: 여러 스레드의 대화를 서버 프록시로 일괄 조회 (CORS 우회)
 * @param {string[]} threadIds - 스레드 ID 배열
 * @returns {Promise<Object>} { [threadId]: conversationArray }
 */
export const fetchConversationsBatchPure = async (threadIds) => {
	if (!Array.isArray(threadIds) || threadIds.length === 0) {
		return {};
	}

	const token = getActiveToken();
	const userID = localStorage.getItem("userID");

	try {
		const response = await axios.post(FETCH_CONVERSATIONS_URL, {
			userID,
			token,
			threadIds,
			fields: CONVERSATION_FIELDS,
		});

		const results = response.data?.results || [];
		return results.reduce((acc, item) => {
			acc[item.threadId] = Array.isArray(item.conversation) ? item.conversation : [];
			return acc;
		}, {});
	} catch (error) {
		console.error("Error fetching conversations batch:", error);
		return {};
	}
};

/**
 * Pure 함수: 스레드의 전체 대화(conversation) 가져오기 (중첩 댓글 포함)
 * 서버 프록시(fetchConversations)를 경유한다.
 * @param {string} threadId - 스레드 ID
 */
export const getConversationPure = async (threadId) => {
	const map = await fetchConversationsBatchPure([threadId]);
	return map[threadId] || [];
};

/**
 * Pure 함수: 최근 3개월 스레드의 대화 데이터 가져오기 (테스트용)
 */
export const fetchRecentConversationsForTest = async (threads) => {
	if (!threads || !Array.isArray(threads) || threads.length === 0) {
		return [];
	}

	// 최근 3개월 내 스레드만 필터링 (이미 필터링된 상태로 받음)
	const recentThreads = threads;

	
	if (recentThreads.length === 0) {
		return [];
	}

	try {
		const allConversations = await Promise.all(
			recentThreads.map(async (thread) => {
				try {
					const conversation = await getConversationPure(thread.id);
					
					return { 
						threadId: thread.id, 
						threadText: thread.text,
						threadTimestamp: thread.timestamp,
						conversation 
					};
				} catch (error) {
					console.error(`❌ Failed to fetch conversation for thread ${thread.id}:`, error);
					return { 
						threadId: thread.id, 
						threadText: thread.text,
						threadTimestamp: thread.timestamp,
						conversation: [] 
					};
				}
			})
		);



		return allConversations;
	} catch (error) {
		console.error("❌ Error fetching recent conversations:", error);
		throw error;
	}
};

/**
 * Pure 함수: 특정 기간의 스레드 데이터만 가져옴
 * @param {number} since - Unix timestamp
 * @param {number} until - Unix timestamp
 * @param {string|null} after - Pagination token
 */
export const fetchThreadsForPeriod = async (since, until, after = null) => {
	const token = getActiveToken();
	if (!token) {
		throw new Error("No access token available for fetchThreadsForPeriod");
	}
	const basePath = "me/threads";

  const params = {
    fields: "id,text,timestamp,media_product_type,username,media_type,media_url,permalink,children{media_type,media_url,alt_text},alt_text,text_attachment,link_attachment_url",
		since: since,
		until: until,
		access_token: token,
		limit: 300,
		...(after && { after }),
	};

	let response;
	try {
		response = after
			? await graphGet(after, {}, { retryOn5xx: true })
			: await graphGet(basePath, { params }, { retryOn5xx: true });
	} catch (error) {
		const wrapped = new Error("Failed to fetch threads for period");
		wrapped.cause = error;
		wrapped.endpoint = after || basePath;
		wrapped.status = error?.response?.status;
		wrapped.responseData = error?.response?.data;
		throw wrapped;
	}

	const threads = response.data.data || [];
  console.log('[RAW] fetchThreadsForPeriod response:', response.data);
	
	// 🔍 API 응답 디버깅 - 미디어 필드 확인
	if (threads.length > 0) {
		console.log('📊 API Response Sample (first thread):', threads[0]);
		console.log('📊 Available fields in response:', Object.keys(threads[0]));
		
		// 미디어 관련 필드 확인
		const sampleThread = threads[0];
		if (sampleThread.media_url || sampleThread.children) {
			console.log('🖼️ Media fields found:', {
				media_url: sampleThread.media_url,
				media_type: sampleThread.media_type,
				children: sampleThread.children,
				alt_text: sampleThread.alt_text
			});
		}
	}
	
	// 각 스레드에 인사이트 데이터 추가
	const threadsWithInsights = await Promise.all(
		threads.map(async (thread) => {
			if (thread.media_type !== "REPOST_FACADE") {
				const insights = await getThreadInsightsPure(thread.id);
				return { ...thread, ...insights };
			} else {
				return { ...thread, views: 0, likes: 0, replies: 0, reposts: 0 };
			}
		})
	);

	return {
		threads: threadsWithInsights.filter((thread) => thread.text),
		paging: response.data.paging
	};
};

/**
 * Pure 함수: 특정 기간의 인사이트 데이터만 가져옴
 * @param {number} since - Unix timestamp
 * @param {number} until - Unix timestamp
 */
export const fetchInsightsForPeriod = async (since, until) => {
	let insightsResponse;
	try {
		insightsResponse = await getInsightPure(since, until);
	} catch (error) {
		const wrapped = new Error("Failed to fetch insights for period");
		wrapped.cause = error;
		wrapped.endpoint = error?.config?.url;
		wrapped.status = error?.response?.status;
		wrapped.responseData = error?.response?.data;
		throw wrapped;
	}
	const data = insightsResponse.data.data || [];

	if (data.length === 0) {
		return {
			views: 0,
			likes: 0,
			replies: 0,
			reposts: 0,
			profileViews: []
		};
	}

	// 메트릭 계산
	const profileViews = data[0]?.values?.map((item) => ({
		x: item.end_time,
		y: item.value,
	})) || [];

	return {
		views: data[0]?.total_value || 0,
		likes: data[2]?.total_value || 0,
		replies: data[3]?.total_value || 0,
		reposts: data[4]?.total_value || 0,
		profileViews
	};
};

/**
 * Pure 함수: 스레드 배열을 주별로 그룹화
 */
const createWeeklyList = (threads) => {
	const weeklyList = threads.reduce((acc, thread) => {
		const threadDate = moment(thread.timestamp);
		const weekTitle = threadDate.week() - threadDate.startOf("month").week() + 1;

		if (!acc[weekTitle]) {
			acc[weekTitle] = [];
		}
		acc[weekTitle].push(thread);
		return acc;
	}, {});

	return Object.entries(weeklyList)
		.map(([title, posts]) => ({ title, posts }))
		.sort((a, b) => moment(b.posts[0].timestamp) - moment(a.posts[0].timestamp));
};

/**
 * Pure 함수: 스레드에 랭킹 정보 추가
 */
const addRankingsToThreads = (threads) => {
	// Sort posts by views and add viewRank
	const viewSorted = [...threads].sort((a, b) => b.views - a.views);
	const viewRanked = viewSorted.map((post, index) => ({
		...post,
		viewRank: index + 1,
	}));

	// Sort posts by engagement score and add engagementRank
	const engagementSorted = [...viewRanked].sort((a, b) => {
		const scoreA = (a.likes * 1 + a.replies * 2) / a.views;
		const scoreB = (b.likes * 1 + b.replies * 2) / b.views;
		return scoreB - scoreA;
	});
	const engagementRanked = engagementSorted.map((post, index) => ({
		...post,
		engagementRank: index + 1,
	}));

	// Sort by timestamp in descending order (newest first)
	return engagementRanked.sort(
		(a, b) => new Date(b.timestamp) - new Date(a.timestamp)
	);
};

/**
 * Pure 함수: 월별 데이터 생성 (Dashboard용)
 */
export const processMonthlyData = (threads, insights, month, year) => {
	if (!threads || threads.length === 0) {
		return {
			threads: {
				month,
				year,
				week: []
			},
			insight: {
				month,
				year,
				views: 0,
				likes: 0,
				replies: 0,
				reposts: 0,
				profileViews: []
			}
		};
	}

	// 스레드에 랭킹 추가
	const rankedThreads = addRankingsToThreads(threads);
	
	// 주별로 그룹화
	const weeklyList = createWeeklyList(rankedThreads);
	
	// 주별 데이터에도 랭킹 적용
	const rankedWeeklyList = weeklyList.map((week) => {
		const rankedPosts = addRankingsToThreads(week.posts);
		return {
			...week,
			posts: rankedPosts,
		};
	});

	// 스레드 메트릭 합계 계산
	const threadMetricSums = threads.reduce(
		(sums, thread) => {
			sums.likes += thread.likes || 0;
			sums.replies += thread.replies || 0;
			sums.reposts += thread.reposts || 0;
			sums.views += thread.views || 0;
			return sums;
		},
		{ likes: 0, replies: 0, reposts: 0, views: 0 }
	);

	return {
		threads: {
			month,
			year,
			week: rankedWeeklyList
		},
		insight: {
			month,
			year,
			...threadMetricSums,
			profileViews: insights.profileViews || []
		}
	};
};

/**
 * 통합 함수: 월별 데이터 완전 가져오기 (API 호출 + 데이터 처리)
 */
export const fetchMonthlyData = async (month, year) => {
	const startDate = moment(`${year}-${month}-01`).startOf('month');
	const endDate = moment(startDate).endOf('month');
	
	// Threads API는 Unix timestamp를 요구함
	const since = startDate.unix();
	// until이 현재 시간보다 미래면 현재 시간으로 제한
	const now = Math.floor(Date.now() / 1000);
	const until = Math.min(endDate.unix(), now);
	
	

	try {
		// 병렬로 스레드와 인사이트 데이터 가져오기
		const [threadsResult, insights] = await Promise.all([
			fetchThreadsForPeriod(since, until),
			fetchInsightsForPeriod(since, until)
		]);

		// 데이터 처리
		return processMonthlyData(threadsResult.threads, insights, month, year);
	} catch (error) {
		console.error(`Error fetching monthly data for ${year}-${month}:`, error);
		const wrapped = new Error(`Monthly data load failed for ${year}-${month}`);
		wrapped.cause = error;
		wrapped.endpoint = error?.endpoint || error?.config?.url;
		wrapped.status = error?.status || error?.response?.status;
		wrapped.responseData = error?.responseData || error?.response?.data;
		throw wrapped;
	}
};

/**
 * Lifetime 데이터에서 월별 데이터 추출
 */
export const extractMonthlyDataFromLifetime = (lifetimeThreads, month, year) => {
	if (!lifetimeThreads || !Array.isArray(lifetimeThreads) || lifetimeThreads.length === 0) {
		return null;
	}

	const startDate = moment(`${year}-${month}-01`).startOf('month');
	const endDate = moment(startDate).endOf('month');

	// 해당 월의 스레드 필터링
	const monthThreads = lifetimeThreads.filter(thread => {
		const threadDate = moment(thread.timestamp);
		return threadDate.isBetween(startDate, endDate, null, '[]');
	});

	if (monthThreads.length === 0) {
		return null;
	}

	// 기본 인사이트 데이터 생성 (실제 profileViews 데이터는 별도 처리 필요)
	const insights = {
		views: monthThreads.reduce((sum, t) => sum + (t.views || 0), 0),
		likes: monthThreads.reduce((sum, t) => sum + (t.likes || 0), 0),
		replies: monthThreads.reduce((sum, t) => sum + (t.replies || 0), 0),
		reposts: monthThreads.reduce((sum, t) => sum + (t.reposts || 0), 0),
		profileViews: [] // 이 부분은 lifetime insight에서 별도 추출 필요
	};

	return processMonthlyData(monthThreads, insights, month, year);
};

// === Firestore + API Hybrid System ===

const DEFAULT_SINCE = 1688540400; // 2023-07-01

// === 로딩 진행률 추적 시스템 ===

/**
 * 로딩 진행률을 localStorage에 저장
 */
const saveLoadingProgress = (userID, progress) => {
  try {
    const progressData = {
      timestamp: Date.now(),
      stage: progress.stage,
      completed: progress.completed || 0,
      total: progress.total || 0,
      lastProcessedId: progress.lastProcessedId || null,
      since: progress.since || null,
      until: progress.until || null
    };
    localStorage.setItem(`loading_progress_${userID}`, JSON.stringify(progressData));
  } catch (error) {
    console.warn('Failed to save loading progress:', error);
  }
};

/**
 * 저장된 로딩 진행률 복구
 */
const getLoadingProgress = (userID) => {
  try {
    const saved = localStorage.getItem(`loading_progress_${userID}`);
    if (!saved) return null;
    
    const progress = JSON.parse(saved);
    // 30분 이내의 진행률만 유효
    if (Date.now() - progress.timestamp > 30 * 60 * 1000) {
      localStorage.removeItem(`loading_progress_${userID}`);
      return null;
    }
    return progress;
  } catch (error) {
    console.warn('Failed to get loading progress:', error);
    return null;
  }
};

/**
 * 로딩 완료 시 진행률 데이터 삭제
 */
const clearLoadingProgress = (userID) => {
  try {
    localStorage.removeItem(`loading_progress_${userID}`);
  } catch (error) {
    console.warn('Failed to clear loading progress:', error);
  }
};

/**
 * 청크 저장 및 진행률 알림 헬퍼 함수
 */
const saveChunkAndNotify = async (userID, threads, onProgress) => {
  if (onProgress) {
    onProgress({
      stage: 'saving',
      message: '데이터 저장 중...',
      isSaving: true
    });
  }
  
  try {
    await updateThreadsToFirestore(userID, threads);
    
    // lastUpdated 즉시 업데이트
    const mostRecentThread = threads[0];
    const lastUpdated = Math.floor(new Date(mostRecentThread.timestamp).getTime() / 1000);
    
    await dbService.collection('user').doc(userID).update({
      lastUpdated
    });
    
    if (onProgress) {
      onProgress({
        stage: 'saved',
        message: '저장 완료',
        lastSavedDate: mostRecentThread.timestamp,
        isSaving: false
      });
    }
  } catch (error) {
    console.error('Chunk save failed:', error);
    if (onProgress) {
      onProgress({
        stage: 'save-error',
        message: '저장 실패',
        error: error.message,
        isSaving: false
      });
    }
    throw error;
  }
};

/**
 * Pure 함수: Firestore + API 하이브리드 라이프타임 스레드 데이터 가져오기
 * 점진적 로딩 지원 (onProgress 콜백)
 */
export const fetchLifetimeThreadsPure = async (userID, onProgress = null) => {
	console.log('🚀 fetchLifetimeThreadsPure with Firestore + API hybrid approach');
	
	const token = getActiveToken();
	if (!token) {
		throw new Error("No access token available for lifetime fetch");
	}
	let allThreads = [];
	let accumulatedThreads = [];
	let nextAfter = null;
	let pageCount = 0;
	let dateRange = { start: null, end: null };
	let estimatedTotalPages = null; // 동적 추정치

	try {
		// 0단계: 연결 시작 알림
		if (onProgress) {
			onProgress({
				stage: 'connecting',
				message: 'Threads 연결 중...',
				pageCount: 0,
				threadCount: 0
			});
		}
		
		// 1단계: 중단된 로딩 진행률 확인
		const savedProgress = getLoadingProgress(userID);
		errorLogger.startTrace({ flow: 'lifetime_load', userID });

		// 1단계: Firestore에서 사용자 문서 확인 (dataFetch.js:390-407)
		const userRef = dbService.collection("user").doc(userID);
		const userDoc = await userRef.get();

		const today = new Date();
		let since = DEFAULT_SINCE; // 1688540400 = 2023-07-01
		let usedLastUpdated = false;
		let until = Math.floor(today.getTime() / 1000);

		// 진행률 저장 시작
		saveLoadingProgress(userID, {
			stage: 'firestore-check',
			since,
			until
		});

		// 2단계: lastUpdated 확인 및 기존 Firestore 데이터 로드 (전체 컬렉션 스캔)
		if (userDoc.exists && userDoc.data().lastUpdated) {
			let lastUpdated = userDoc.data().lastUpdated;
			// 타입 안전 처리 (ISO 문자열 → Unix 초)
			if (typeof lastUpdated === 'string') {
				lastUpdated = Math.floor(new Date(lastUpdated).getTime() / 1000);
			}
			since = lastUpdated; // 저장된 기준 시각
			usedLastUpdated = true;
			
			// threads 컬렉션의 모든 문서 가져오기
			const threadsSnapshot = await userRef.collection("threads").get();
			
			allThreads = [];
			let documentCount = 0;
			
			threadsSnapshot.forEach(doc => {
				if (doc.exists) {
					const data = doc.data();
					if (data.list && Array.isArray(data.list)) {
						allThreads.push(...data.list);
						documentCount++;
						console.log(`📄 Document ${doc.id}: ${data.list.length} threads`);
					}
				}
			});
			
			console.log(`✅ Total threads loaded from ${documentCount} documents: ${allThreads.length}`);
		}

		// 3단계: API에서 since 이후의 새 데이터만 가져오기
		const basePath = "me/threads";
		
		console.log('🔍 자동 업데이트 API 호출 파라미터:', {
			since: since,
			until: until,
			sinceDate: new Date(since * 1000).toISOString(),
			untilDate: new Date(until * 1000).toISOString(),
			timeRange: `${Math.floor((until - since) / 60 / 60 / 24)}일간`
		});

			do {
      const params = {
        fields: "id,text,timestamp,media_product_type,username,media_type,media_url,permalink,children{media_type,media_url,alt_text},alt_text,text_attachment,link_attachment_url",
					since: usedLastUpdated ? (since + 1) : since, // 🎯 경계 포함 방지(+1초)
				until: until,
				access_token: token,
				limit: 100,
				...(nextAfter && { after: nextAfter }),
			};
			
			const response = nextAfter
				? await graphGet(nextAfter, {}, { retryOn5xx: true })
				: await graphGet(basePath, { params }, { retryOn5xx: true });

      const responseThreads = response.data.data || [];
      console.log('[RAW] fetchLifetimeThreadsPure page response:', {
        page: pageCount + 1,
        data: response.data
      });
			
			console.log('📡 API 응답:', {
				responseCount: responseThreads.length,
				hasNext: !!response.data.paging?.next,
				firstThreadTimestamp: responseThreads[0]?.timestamp ? new Date(responseThreads[0].timestamp).toISOString() : null,
				lastThreadTimestamp: responseThreads[responseThreads.length - 1]?.timestamp ? new Date(responseThreads[responseThreads.length - 1].timestamp).toISOString() : null
			});
			
			// 🔍 Lifetime API 미디어 필드 디버깅
			if (responseThreads.length > 0) {
				console.log('🔍 Lifetime API Response Sample:', responseThreads[0]);
				const mediaThreads = responseThreads.filter(t => t.media_url || t.children);
				if (mediaThreads.length > 0) {
					console.log('🖼️ Found threads with media:', mediaThreads.length);
					console.log('🖼️ Sample media thread:', mediaThreads[0]);
				} else {
					console.log('⚠️ No threads with media found in this batch');
				}
			}

			if (responseThreads.length > 0) {
				// 날짜 범위 업데이트 (매 페이지마다)
				if (pageCount === 0) {
					// 첫 페이지: start와 end 모두 설정
					dateRange.start = responseThreads[0].timestamp;
					dateRange.end = responseThreads[responseThreads.length - 1].timestamp;
				} else {
					// 이후 페이지: end만 업데이트 (더 오래된 날짜로)
					dateRange.end = responseThreads[responseThreads.length - 1].timestamp;
				}
				
				// 배치 처리로 인사이트 가져오기
				const threadsWithInsights = await getBatchThreadInsights(responseThreads);

				// 새로운 필터링 함수 사용 (텍스트 또는 미디어 포함)
				const filteredThreads = filterThreadsForStorage(threadsWithInsights);
				
				console.log('✅ 필터링 후 스레드:', {
					original: responseThreads.length,
					afterInsights: threadsWithInsights.length,
					afterFilter: filteredThreads.length
				});

				// 🔥 미디어가 있는 스레드들을 프록시로 처리
				console.log('📥 Processing media for new threads with proxy...');
				const threadsWithDownloadedMedia = filteredThreads.map((thread) => {
					// 🔍 children 구조 분석 - Threads API는 {data: []} 형태
					let childrenArray = [];
					if (thread.children && thread.children.data && Array.isArray(thread.children.data)) {
						childrenArray = thread.children.data;
					}

					if (thread.media_url || childrenArray.length > 0) {
						// 미디어 정보 구조화
						const processedThread = {
							...thread,
							hasMedia: true,
							mediaUrls: childrenArray.length > 0 
								? childrenArray.filter(child => child.media_url).map(child => ({
									url: child.media_url,
									type: child.media_type,
									alt: child.alt_text || ''
								}))
								: [{
									url: thread.media_url,
									type: thread.media_type,
									alt: thread.alt_text || ''
								}],
							isCarousel: !!(childrenArray.length > 1)
						};
						
						console.log(`🔄 Using proxy for media in new thread: ${thread.id}`, {
							mediaCount: processedThread.mediaUrls.length,
							isCarousel: processedThread.isCarousel
						});
						
						try {
							return processThreadMediaWithProxy(processedThread);
						} catch (error) {
							console.error(`❌ Failed to process proxy for thread ${thread.id}:`, error);
							return processedThread; // 프록시 실패 시 원본 반환
						}
					}
					return thread;
				});

				// 프록시 처리 결과 로그
				const proxiedThreadsCount = threadsWithDownloadedMedia.filter(t => t.usingProxy).length;
				console.log(`✅ Proxy processing complete for batch. Threads with proxy: ${proxiedThreadsCount}/${threadsWithDownloadedMedia.length}`);
				
				// 점진적 로딩: accumulatedThreads에 누적
				accumulatedThreads = [...accumulatedThreads, ...threadsWithDownloadedMedia];
				pageCount++;

				// ❌ 매 페이지마다 lastUpdated 업데이트 제거 (중복 저장 방지)
				// lastUpdated는 실제 저장 성공 후에만 업데이트하도록 saveChunkAndNotify에서 처리
				console.log(`📦 Processed page ${pageCount} with ${threadsWithDownloadedMedia.length} threads`);
				
				// 프로그레스 계산 (첫 페이지에서 추정치 설정)
				if (pageCount === 1 && !estimatedTotalPages) {
					// 첫 페이지 기준으로 대략적인 총 페이지 수 추정
					// 평균적으로 사용자는 100-500개의 스레드를 가지고 있고, 페이지당 100개씩
					const avgThreadsPerUser = 300;
					estimatedTotalPages = Math.max(3, Math.ceil(avgThreadsPerUser / 100));
				}
				
				// 동적 프로그레스 계산 (90%를 넘지 않도록)
				const currentProgress = estimatedTotalPages 
					? Math.min(90, calculateProgress(pageCount, estimatedTotalPages))
					: Math.min(90, pageCount * 15); // 페이지당 15% 증가, 최대 90%

				// 즉시 프론트엔드 반영
				if (onProgress) {
					onProgress({
						stage: 'page-loaded',
						message: dateRange.start && dateRange.end ? 
							`${formatProgressDateRange(dateRange.start, dateRange.end)} 데이터를 불러오는 중...` : 
							'스레드 데이터를 불러오는 중...',
						dateRange,
						pageCount,
						threadCount: accumulatedThreads.length,
						threads: [...accumulatedThreads], // 복사본 전달
						progress: currentProgress
					});
				}
				
				// 950KB 도달 시 청크 저장 (저장 전 ID 기반 중복 제거)
				if (shouldSaveChunk(accumulatedThreads)) {
					const existingIds = new Set(allThreads.map(t => t.id));
					const uniqueNewThreads = accumulatedThreads.filter(thread => thread && !existingIds.has(thread.id));
					if (uniqueNewThreads.length > 0) {
						await saveChunkAndNotify(userID, uniqueNewThreads, onProgress);
						allThreads = [...allThreads, ...uniqueNewThreads];
					} else {
						console.log('⚠️ 모든 스레드가 이미 존재함 - 청크 저장 스킵');
					}
					accumulatedThreads = []; // 메모리 정리
				}
			}
			
			nextAfter = response.data.paging?.next || null;
			errorLogger.trace('paging_progress', { hasNext: !!nextAfter, batchCount: responseThreads.length, since, until });
		} while (nextAfter);
		
		console.log('🎯 최종 새 스레드 결과:', {
			accumulatedThreadsCount: accumulatedThreads.length,
			existingThreadsCount: allThreads.length,
			willSaveToFirestore: accumulatedThreads.length > 0
		});

		// 남은 데이터 최종 저장 (저장 전 ID 기반 중복 제거)
		if (accumulatedThreads.length > 0) {
			const existingIds = new Set(allThreads.map(t => t.id));
			const uniqueNewThreads = accumulatedThreads.filter(thread => thread && !existingIds.has(thread.id));
			if (uniqueNewThreads.length > 0) {
				await saveChunkAndNotify(userID, uniqueNewThreads, onProgress);
				allThreads = [...allThreads, ...uniqueNewThreads];
			} else {
				console.log('⚠️ 모든 스레드가 이미 존재함 - 최종 저장 스킵');
			}
		}

		// 저장 후 실제 Firestore 상태로 다시 로드 (전체 컬렉션 스캔)
		const threadsSnapshot = await userRef.collection('threads').get();
		allThreads = [];
		let documentCount = 0;
		threadsSnapshot.forEach(doc => {
			if (doc.exists) {
				const data = doc.data();
				if (data.list && Array.isArray(data.list)) {
					allThreads.push(...data.list);
					documentCount++;
					console.log(`📄 Document ${doc.id}: ${data.list.length} threads`);
				}
			}
		});
		console.log(`✅ Total threads reloaded from ${documentCount} documents: ${allThreads.length}`);

		// 날짜순 정렬
		const sortedThreads = allThreads.sort((a, b) =>
			moment(b.timestamp).diff(moment(a.timestamp))
		);
		
		// 완료 알림
		if (onProgress) {
			onProgress({
				stage: 'complete',
				message: '데이터 로드 완료',
				threadCount: sortedThreads.length,
				threads: sortedThreads,
				progress: 100 // 완료 시 100%
			});
		}
		
		// 로딩 완료 시 진행률 데이터 삭제
		clearLoadingProgress(userID);
		
		return sortedThreads;
		
	} catch (error) {
		console.error("Error fetching lifetime threads:", error);
		try {
			await errorLogger.flushOnError({
				component: 'fetchLifetimeThreadsPure',
				event: 'lifetime_load_failed',
				status: error?.response?.status,
				endpoint: 'me/threads',
				stack: error?.stack
			});
		} catch (_) {}
		// 에러 시 기존 Firestore 데이터라도 반환
		if (allThreads.length > 0) {
			return allThreads;
		}
		throw error;
	}
};

/**
 * Pure 함수: 라이프타임 인사이트 데이터 가져오기 (순수 API 호출)
 */
export const fetchLifetimeInsightsPure = async () => {
	// Threads insights API는 `since`가 API 최소 시각(2024-04-13) 이후이면서
	// 현재로부터 약 2년 이내여야 한다. 시작일을 고정하면 시간이 지나면서
	// 구간이 2년을 초과해 500 오류가 발생하므로 롤링 윈도우로 클램프한다.
	const apiFloor = moment.unix(1712991600); // 2024-04-13, Threads insights 최소 시각
	const rollingFloor = moment().subtract(2, "years").add(7, "days"); // 2년 한계 + 버퍼
	const insightSince = moment.max(apiFloor, rollingFloor).startOf("day").unix();
	const until = Math.floor(new Date().getTime() / 1000);

	try {
		const insightsResponse = await getInsightPure(insightSince, until);
		const data = insightsResponse.data.data || [];
		
		if (data.length === 0) {
			return {
				profileViewsTotal: 0,
				profileViews: [],
				followers: 0,
				likes: 0,
				replies: 0,
				reposts: 0,
			};
		}

		const totalViews = data[0].values.reduce(
			(sum, item) => sum + item.value,
			0
		);
		
		const profileViews = data[0].values.map((item) => ({
			x: item.end_time,
			y: item.value,
		}));

		const filteredProfileViews = profileViews.reduce((acc, curr) => {
			if (acc.length === 0 && curr.y !== 0) {
				acc.push(curr);
			} else if (acc.length > 0) {
				acc.push(curr);
			}
			return acc;
		}, []);

		return {
			profileViewsTotal: totalViews,
			profileViews: filteredProfileViews,
			followers: data[1]?.total_value || 0,
			likes: data[2]?.total_value || 0,
			replies: data[3]?.total_value || 0,
			reposts: data[4]?.total_value || 0,
		};
	} catch (error) {
		console.error("Error fetching lifetime insights:", error);
		throw error;
	}
};

/**
 * Pure 함수: 주간 참여도 데이터 계산
 */
export const computeWeeklyEngagement = (threads) => {
	if (!threads || !Array.isArray(threads) || threads.length === 0) {
		return [];
	}

	const weeklyEngagement = threads.reduce((acc, thread) => {
		const threadDate = moment(thread.timestamp);
		const weekStart = threadDate.startOf("week");
		const weekKey = weekStart.format("YYYY-MM-DD");

		let weekData = acc.find((item) => item.date === weekKey);
		if (!weekData) {
			weekData = {
				date: weekKey,
				year: weekStart.year(),
				month: weekStart.month() + 1,
				week: weekStart.week(),
				likes: 0,
				replies: 0,
				reposts: 0,
				views: 0,
				engagementRate: 0,
			};
			acc.push(weekData);
		}

		weekData.likes += thread.likes || 0;
		weekData.replies += thread.replies || 0;
		weekData.reposts += thread.reposts || 0;
		weekData.views += thread.views || 0;

		return acc;
	}, []);

	weeklyEngagement.forEach((week) => {
		week.engagementRate =
			week.views > 0
				? ((week.likes * 1 + week.replies * 2 + week.reposts * 3) /
						week.views) *
				  100
				: 0;
	});

	const sortedWeeklyEngagement = weeklyEngagement.sort((a, b) =>
		moment(a.date).diff(moment(b.date))
	);

	// 차트 형식으로 변환
	return [
		sortedWeeklyEngagement.map((week) => ({
			date: new Date(week.date),
			value: week.likes,
		})),
		sortedWeeklyEngagement.map((week) => ({
			date: new Date(week.date),
			value: week.replies,
		})),
		sortedWeeklyEngagement.map((week) => ({
			date: new Date(week.date),
			value: week.reposts,
		})),
		sortedWeeklyEngagement.map((week) => ({
			date: new Date(week.date),
			value: week.engagementRate,
		})),
	];
};

/**
 * Pure 함수: 모든 스레드의 댓글 데이터 가져오기 (기존 방식 - 호환성 유지)
 */
export const fetchAllRepliesPure = async (threads) => {
	if (!threads || !Array.isArray(threads) || threads.length === 0) {
		return [];
	}

	try {
		const allReplies = await Promise.all(
			threads.map(async (thread) => {
				try {
					const replies = await getRepliesPure(thread.id);
					return { threadId: thread.id, replies };
				} catch (error) {
					console.error(
						`Failed to fetch replies for thread ${thread.id}:`,
						error
					);
					return { threadId: thread.id, replies: [] };
				}
			})
		);

		return allReplies;
	} catch (error) {
		console.error("Error fetching all replies:", error);
		throw error;
	}
};

/**
 * Pure 함수: 모든 스레드의 대화(conversation) 데이터 가져오기 (새로운 방식)
 * @param {array} threads - 스레드 목록
 * @param {function} onProgress - 점진적 로딩 콜백 ({ completed, total, conversations, usernames })
 */
export const fetchAllConversationsPure = async (threads, onProgress = null) => {
	if (!threads || !Array.isArray(threads) || threads.length === 0) {
		return [];
	}

	const allConversations = [];
	const usernamesFound = new Set();

	// 헬퍼: 단일 스레드 결과 객체 생성
	const buildResult = (thread, conversation) => ({
		threadId: thread.id,
		threadText: thread.text,
		threadTimestamp: thread.timestamp,
		conversation: Array.isArray(conversation) ? conversation : [],
	});

	// 헬퍼: 결과에서 유저네임 추출
	const extractUsernames = (result) => {
		result.conversation.forEach(reply => {
			if (reply.username) usernamesFound.add(reply.username);
		});
	};

	try {
		// === 청크 단위 배치 처리 (서버 프록시 경유) ===
		// 요청당 스레드 수를 제한해 함수 타임아웃/요청 크기를 안전하게 유지하고,
		// 청크가 끝날 때마다 진행률 콜백으로 UI에 친구 이름을 점진적으로 노출한다.
		const CHUNK_SIZE = 25;

		for (let i = 0; i < threads.length; i += CHUNK_SIZE) {
			const chunk = threads.slice(i, i + CHUNK_SIZE);
			const conversationMap = await fetchConversationsBatchPure(chunk.map(t => t.id));

			chunk.forEach(thread => {
				const result = buildResult(thread, conversationMap[thread.id]);
				allConversations.push(result);
				extractUsernames(result);
			});

			// 점진적 콜백 (UI에 친구 이름 표시)
			if (onProgress) {
				onProgress({
					completed: allConversations.length,
					total: threads.length,
					conversations: [...allConversations],
					usernames: Array.from(usernamesFound),
				});
			}
		}

		return allConversations;
	} catch (error) {
		console.error("Error fetching all conversations:", error);
		throw error;
	}
};

/**
 * Pure 함수: 댓글별 사용자 데이터 계산 (기존 방식 - 호환성 유지)
 */
export const computeRepliesByUser = (repliesData) => {
	if (!repliesData || !Array.isArray(repliesData)) {
		return [];
	}

	const userRepliesMap = {};

	repliesData.forEach(({ replies }) => {
		if (replies && Array.isArray(replies)) {
			replies.forEach((reply) => {
				const username = reply.username || 'anonymous';
				if (!userRepliesMap[username]) {
					userRepliesMap[username] = 0;
				}
				userRepliesMap[username]++;
			});
		}
	});

	return Object.entries(userRepliesMap)
		.map(([username, count]) => ({ username, count }))
		.sort((a, b) => b.count - a.count);
};

/**
 * Pure 함수: Conversation 데이터에서 사용자별 상호작용 분석 (새로운 방식)
 * 같은 스레드 내에서 서로 나눈 대화를 시간순으로 분석
 */
export const analyzeUserInteractions = (conversationsData) => {
	if (!conversationsData || !Array.isArray(conversationsData)) {
		return [];
	}

	// 현재 사용자 정보 (모두 String으로 정규화)
	const myUserId = String(localStorage.getItem("userID") || '');
	const myProfile = JSON.parse(localStorage.getItem('userProfile') || '{}');
	const myProfileId = String(myProfile?.id || '');
	const myUsername = String(myProfile?.username || '').toLowerCase();
	const userInteractionsMap = {};

	console.log('🔍 Analyzing user interactions with myUserId:', myUserId, 'myProfileId:', myProfileId, 'myUsername:', myUsername);
	console.log(`📅 Processing ${conversationsData?.length || 0} conversations`);

	conversationsData.forEach(({ threadId, conversation, threadText, threadTimestamp }) => {
		if (!conversation || !Array.isArray(conversation)) return;

		// Step 1: 다른 사용자들의 댓글 찾기 (자신 제외 - ID와 username 모두 체크)
		const othersComments = conversation.filter(comment => {
			const commentUserId = String(comment.user_id || '');
			const commentUsername = String(comment.username || '').toLowerCase();
			// 자기 자신 제외 (userID, profileId, username 모두 체크)
			if (myUserId && commentUserId === myUserId) return false;
			if (myProfileId && commentUserId === myProfileId) return false;
			if (myUsername && commentUsername === myUsername) return false;
			return comment.username; // username이 있어야 함
		});

		// Step 2: 내가 단 댓글/대댓글 찾기
		const myComments = conversation.filter(comment => {
			const commentUserId = String(comment.user_id || '');
			return (myUserId && commentUserId === myUserId) ||
				(myProfileId && commentUserId === myProfileId) ||
				comment.is_reply_owned_by_me;
		});



		// Step 3: 각 사용자별로 상호작용 매핑
		othersComments.forEach(otherComment => {
			const username = otherComment.username;
			const oderId = otherComment.user_id || otherComment.id || '';

			if (!userInteractionsMap[username]) {
				userInteractionsMap[username] = {
					username,
					userId: oderId,  // ← userId 추가!
					conversations: [], // 시간순 대화 배열
					totalInteractions: 0,
					theirCommentsCount: 0,
					myRepliesCount: 0
				};
			}

			// 그 사용자의 댓글 추가
			userInteractionsMap[username].conversations.push({
				type: 'their_comment',
				threadId,
				threadText: threadText?.substring(0, 100) + '...',
				threadTimestamp,
				comment: {
					...otherComment,
					permalink: otherComment.permalink || `https://www.threads.net/@${otherComment.username}`,
				},
				timestamp: otherComment.timestamp
			});
			userInteractionsMap[username].theirCommentsCount++;

			// 내가 이 댓글에 대댓글을 달았는지 확인
			const myRepliesToThis = myComments.filter(myComment => {
				const repliedToId = typeof myComment.replied_to === 'object' 
					? myComment.replied_to?.id 
					: myComment.replied_to;
				return repliedToId === otherComment.id;
			});



			// 내 대댓글들도 추가
			myRepliesToThis.forEach(myReply => {
				userInteractionsMap[username].conversations.push({
					type: 'my_reply',
					threadId,
					threadText: threadText?.substring(0, 100) + '...',
					threadTimestamp,
					comment: {
						...myReply,
						permalink: myReply.permalink || `https://www.threads.net/`
					},
					timestamp: myReply.timestamp
				});
				userInteractionsMap[username].myRepliesCount++;
			});
		});
	});

	// Step 4: 각 사용자별로 대화를 시간순 정렬하고 통계 계산
	const result = Object.values(userInteractionsMap).map(user => ({
		...user,
		conversations: user.conversations.sort((a, b) => 
			new Date(a.timestamp) - new Date(b.timestamp)
		),
		totalInteractions: user.theirCommentsCount + user.myRepliesCount,
		count: user.theirCommentsCount + user.myRepliesCount // 기존 UI 호환성
	})).sort((a, b) => b.totalInteractions - a.totalInteractions);


	return result;
};

/**
 * Pure 함수: 박스플롯 데이터 계산
 */
export const computeBoxPlotData = (threads) => {
	if (!threads || !Array.isArray(threads) || threads.length === 0) {
		return {};
	}

	const metrics = ['views', 'likes', 'replies', 'reposts'];
	const boxPlotData = {};

	metrics.forEach(metric => {
		const values = threads
			.map(thread => thread[metric] || 0)
			.filter(value => value >= 0)
			.sort((a, b) => a - b);

		if (values.length === 0) {
			boxPlotData[metric] = { min: 0, max: 0, avg: 0 };
			return;
		}

		const min = values[0];
		const max = values[values.length - 1];
		const avg = values.reduce((sum, val) => sum + val, 0) / values.length;

		boxPlotData[metric] = { min, max, avg: Math.round(avg) };
	});

	return boxPlotData;
};

/**
 * 날짜 범위 포맷팅 유틸리티
 */
const formatDateRange = (since, isFromCache) => {
	if (isFromCache) {
		const sinceDate = new Date(since * 1000);
		return i18n.t('loading.since_update', { 
			year: sinceDate.getFullYear(), 
			month: sinceDate.getMonth() + 1 
		});
	} else {
		return i18n.t('loading.full_period');
	}
};

/**
 * 1주일 이내의 스레드 필터링 및 인사이트 업데이트
 * @param {Array} threads - 전체 스레드 배열
 * @returns {Promise<Object>} 업데이트 결과
 */
export const updateRecentThreadsInsights = async (threads) => {
	if (!threads || !Array.isArray(threads) || threads.length === 0) {
		return { 
			updatedThreads: [], 
			count: 0,
			message: 'No threads to update'
		};
	}

	const oneWeekAgo = moment().subtract(7, 'days').unix();
	
	// 1주일 이내의 스레드 필터링
	const recentThreads = threads.filter(thread => {
		const threadTimestamp = moment(thread.timestamp).unix();
		return threadTimestamp >= oneWeekAgo;
	});

	console.log(`🔄 Found ${recentThreads.length} threads from the last 7 days to update`);

	if (recentThreads.length === 0) {
		return { 
			updatedThreads: [], 
			count: 0,
			message: 'No recent threads found'
		};
	}

	try {
		// 배치로 인사이트 업데이트
		const updatedThreads = await getBatchThreadInsights(
			recentThreads, 
			25, // 작은 청크 사이즈로 API 부하 줄이기
			(progress) => {
				console.log(`📊 Updating insights: ${progress.completed}/${progress.total} batches`);
			}
		);

		console.log(`✅ Successfully updated insights for ${updatedThreads.length} recent threads`);

		return {
			updatedThreads,
			count: updatedThreads.length,
			message: `Updated ${updatedThreads.length} threads from the last 7 days`
		};
	} catch (error) {
		console.error('❌ Error updating recent threads insights:', error);
		throw error;
	}
};

/**
 * Firestore에서 스레드 업데이트 (sharding 고려)
 * @param {string} userID - 사용자 ID
 * @param {Array} updatedThreads - 업데이트된 스레드 배열
 * @returns {Promise<void>}
 */
export const updateThreadsInFirestore = async (userID, updatedThreads) => {
	if (!updatedThreads || updatedThreads.length === 0) {
		console.log('No threads to update in Firestore');
		return;
	}

	const userRef = dbService.collection("user").doc(userID);
	
	try {
		// 1. 메타데이터 확인
		const userDoc = await userRef.get();
		
		// 2. threads 컬렉션의 모든 문서 가져오기 (전체 스캔)
		const threadsSnapshot = await userRef.collection("threads").get();
		let allDocuments = [];
		
		threadsSnapshot.forEach(doc => {
			if (doc.exists) {
				const data = doc.data();
				if (data.list && Array.isArray(data.list)) {
					allDocuments.push({
						id: doc.id,
						data: data.list
					});
				}
			}
		});

		// 3. 업데이트된 스레드로 기존 스레드 교체
		const updatedThreadIds = new Set(updatedThreads.map(t => t.id));
		let updateOperations = [];

		for (const doc of allDocuments) {
			let hasUpdates = false;
			const updatedList = doc.data.map(thread => {
				if (updatedThreadIds.has(thread.id)) {
					hasUpdates = true;
					const updatedThread = updatedThreads.find(t => t.id === thread.id);
					return updatedThread;
				}
				return thread;
			});

			if (hasUpdates) {
				updateOperations.push({
					docId: doc.id,
					data: updatedList
				});
			}
		}

		// 4. Firestore 업데이트 실행
		for (const operation of updateOperations) {
			await userRef.collection("threads").doc(operation.docId).update({
				list: operation.data,
				lastModified: new Date().toISOString()
			});
			
			console.log(`✅ Updated document ${operation.docId} with recent thread insights`);
		}

		// 5. 사용자 문서의 lastUpdated는 메인 함수에서 처리
		// (중복 업데이트 방지)

		console.log(`🎉 Successfully updated ${updatedThreads.length} threads in Firestore`);

	} catch (error) {
		console.error('❌ Error updating threads in Firestore:', error);
		throw error;
	}
};

/**
 * 최적화된 라이프타임 데이터 로딩 (Firestore 캐시 + API 업데이트)
 * @param {string} userID - 사용자 ID
 * @param {Function} onProgress - 진행 상황 콜백
 * @param {boolean} forceUpdate - 캐시를 무시하고 강제 업데이트 (기본값: false)
 */
export const fetchLifetimeThreadsOptimized = async (userID, onProgress = null, forceUpdate = false) => {
	console.log("🔍 DEBUG: fetchLifetimeThreadsOptimized 진입", { userID, forceUpdate });
	try {
		// 1단계: Firestore에서 캐시된 데이터 즉시 확인
		if (onProgress) onProgress({
			stage: 'cache-check',
			message: i18n.t('loading.checking_cache'),
			subMessage: i18n.t('loading.firestore_cache_check')
		});

		const userRef = dbService.collection("user").doc(userID);
		const userDoc = await userRef.get();
		
		let cachedThreads = [];
		let dateRangeText = '';
		
		if (userDoc.exists) {
			// 메타데이터 확인 및 다중 문서 로딩
			const userData = userDoc.data();
			
			// threads 컬렉션의 모든 문서 가져오기 (전체 스캔)
			const threadsSnapshot = await userRef.collection("threads").get();
			
			cachedThreads = [];
			let documentCount = 0;
			
			threadsSnapshot.forEach(doc => {
				if (doc.exists) {
					const data = doc.data();
					if (data.list && Array.isArray(data.list)) {
						cachedThreads.push(...data.list);
						documentCount++;
						console.log(`📄 Cache document ${doc.id}: ${data.list.length} threads`);
					}
				}
			});
			
			console.log(`✅ Total cached threads from ${documentCount} documents: ${cachedThreads.length}`);
			
			// lastUpdated가 있으면 캐시된 데이터 우선 반환
			if (userData?.lastUpdated && cachedThreads.length > 0) {
				dateRangeText = formatDateRange(userData.lastUpdated, true);
				
				if (onProgress) onProgress({ 
					stage: 'cached', 
					message: i18n.t('loading.cache_loaded'),
					subMessage: `${dateRangeText} - ${i18n.t('loading.threads_found', { count: cachedThreads.length })}`,
					data: cachedThreads 
				});
				
				// 캐시된 데이터가 있고 lastUpdated가 최근(2시간 이내)이면 즉시 반환
				let lastUpdated = userData.lastUpdated;
				const now = Math.floor(Date.now() / 1000);
				
				// ✅ lastUpdated 타입 안전 처리 (기존 ISO 문자열 대응)
				if (typeof lastUpdated === 'string') {
					lastUpdated = Math.floor(new Date(lastUpdated).getTime() / 1000);
					console.log("⚠️ lastUpdated ISO 문자열 감지, Unix timestamp로 변환:", lastUpdated);
				}
				
				const dayInSeconds = 2 * 60 * 60; // 2시간
				const timeDiff = now - lastUpdated;
				
				console.log("🔍 DEBUG: 캐시 신선도 체크", {
					lastUpdated,
					now,
					timeDiff,
					timeDiffHours: timeDiff / 3600,
					dayInSeconds,
					isFresh: timeDiff < dayInSeconds,
					hasThreads: cachedThreads.length > 0,
					forceUpdate
				});
				
				// 항상 최신 데이터 확인을 위해 API 증분 업데이트 진행
				console.log("🔍 DEBUG: 캐시가 있어도 최신 여부 확인 위해 API 호출 진행", { 
					forceUpdate,
					cachedCount: cachedThreads.length
				});
			}
		}
		
		// lastUpdated가 없거나 오래된 경우에만 전체 업데이트
		if (!userDoc.exists || !userDoc.data().lastUpdated) {
			dateRangeText = formatDateRange(DEFAULT_SINCE, false);
		}
		
		// 2단계: 캐시 표시 후에도 항상 API 증분 업데이트 시도
		console.log("🔍 DEBUG: 캐시 체크 후 API 호출 결정 (항상 증분 업데이트 시도)");
		console.log("🔍 DEBUG: fetchLifetimeThreadsPure 호출 직전 - 최신 데이터 확인");
		if (onProgress) onProgress({ 
			stage: 'updating', 
			message: i18n.t('loading.loading_threads'),
			subMessage: dateRangeText
		});
		
		// onProgress 콜백을 fetchLifetimeThreadsPure에 전달
		const updatedData = await fetchLifetimeThreadsPure(userID, onProgress);
		console.log("🔍 DEBUG: fetchLifetimeThreadsPure 완료", { dataLength: updatedData?.length });
		
		if (onProgress) onProgress({ 
			stage: 'complete', 
			message: i18n.t('loading.data_updated'),
			subMessage: i18n.t('loading.total_threads', { count: updatedData.length }),
			data: updatedData 
		});
		
		return updatedData;
		
	} catch (error) {
		console.error('Optimized data loading failed:', error);
		
		// 에러 시 Firestore 캐시된 데이터라도 반환 시도 (다중 문서 지원)
		try {
			const userRef = dbService.collection("user").doc(userID);
			const userDoc = await userRef.get();
			let fallbackData = [];
			
			if (userDoc.exists) {
				
				// threads 컬렉션의 모든 문서 가져오기 (전체 스캔)
				const threadsSnapshot = await userRef.collection("threads").get();
				
				threadsSnapshot.forEach(doc => {
					if (doc.exists) {
						const data = doc.data();
						if (data.list && Array.isArray(data.list)) {
							fallbackData.push(...data.list);
						}
					}
				});
				
				if (fallbackData.length > 0) {
					if (onProgress) onProgress({ 
						stage: 'error', 
						message: `Update failed, using ${fallbackData.length} cached threads`, 
						data: fallbackData 
					});
					return fallbackData;
				}
			}
		} catch (fallbackError) {
			console.error('Failed to load fallback data:', fallbackError);
		}
		
		throw error;
	}
};

// ==================================================================================
// === LEGACY FUNCTIONS (from dataFetch.js) - 이전 버전 호환성을 위한 레거시 함수들 ===
// ==================================================================================

/**
 * 비동기 Firestore 저장 함수 (백그라운드 실행)
 * @deprecated - 레거시 함수, 현재 사용되지 않음
 */
const saveInsightsToFirestoreAsync = async (userRef, lifetimeInsight, userID) => {
	try {
		await userRef.collection("insight").doc("insight").set(
			{ list: lifetimeInsight },
			{ merge: true }
		);
	} catch (error) {
		console.error("⚠️ Failed to save insights to Firestore (non-critical):", error);
		try {
			const errorLogRef = dbService.collection("errorLogs").doc(userID);
			await errorLogRef.update({
				errors: arrayUnion({
					type: "firestore_insights_save",
					error: error.message,
					timestamp: new Date(),
				}),
			});
		} catch (logError) {
			console.error("Failed to log error:", logError);
		}
	}
};

/**
 * Legacy: 사용자 프로필 조회 (특정 ID)
 * @deprecated - 현재 사용되지 않음
 */
export const getUserProfileLegacy = async (id) => {
	const token = getActiveToken();
	return await graphGet(`${id}`, {
		params: {
			fields: "id,username,name,threads_profile_picture_url,threads_biography",
			access_token: token,
		},
	});
};

/**
 * Legacy: 특정 월의 profileViews만 가져오는 함수
 * @deprecated - 현재 사용되지 않음, fetchMonthlyData 함수 사용 권장
 */
export const fetchMonthlyProfileViewsLegacy = async (year, month) => {
	try {
		const startDate = moment(`${year}-${month}-01`).startOf('month');
		const endDate = moment(startDate).endOf('month');
		
		const since = startDate.format('YYYY-MM-DD');
		const until = endDate.format('YYYY-MM-DD');
		
		const insightsResponse = await getInsightPure(since, until);
		
		if (!insightsResponse || !insightsResponse.data || !insightsResponse.data.data || insightsResponse.data.data.length === 0) {
			return null;
		}
		
		const monthlyView = insightsResponse.data.data[0].values.map((item) => ({
			x: item.end_time,
			y: item.value,
		}));
		
		return {
			month,
			year,
			profileViews: monthlyView
		};
	} catch (error) {
		console.error(`Error fetching profileViews for ${year}-${month}:`, error);
		return null;
	}
};

/**
 * Legacy: 개별 스레드 인사이트 가져오기 (재시도 로직 없음)
 * @deprecated - getThreadInsightsPure 사용 권장
 */
export const getThreadInsightsLegacy = async (threadId) => {
	const token = getActiveToken();
	try {
		const response = await graphGet(
			`${threadId}/insights`,
			{
				params: {
					metric: "views, likes,replies,reposts",
					access_token: token,
				},
			},
			{ retryOn5xx: false }
		);
		return response.data.data.reduce((acc, item) => {
			acc[item.name] = item.values[0].value;
			return acc;
		}, {});
	} catch (error) {
		console.error(`Error fetching insights for thread ${threadId}:`, error);
		return {};
	}
};

/**
 * Legacy: 스레드 배열을 주별로 그룹화 (구버전)
 * @deprecated - 내부 createWeeklyList 함수 사용 권장
 */
export const createWeeklyListLegacy = (threads) => {
	const weeklyList = threads.reduce((acc, thread) => {
		const threadDate = moment(thread.timestamp);
		const weekTitle =
			threadDate.week() - threadDate.startOf("month").week() + 1;

		if (!acc[weekTitle]) {
			acc[weekTitle] = [];
		}
		acc[weekTitle].push(thread);
		return acc;
	}, {});

	return Object.entries(weeklyList)
		.map(([title, posts]) => ({ title, posts }))
		.sort(
			(a, b) => moment(b.posts[0].timestamp) - moment(a.posts[0].timestamp)
		);
};

/**
 * Legacy: 월별 스레드 데이터 가져오기 (구버전 - 복잡한 상태 관리 포함)
 * @deprecated - fetchMonthlyData 함수 사용 권장
 */
export const fetchMonthlyThreadsLegacy = async (
	after = null,
	since,
	until,
	setPostLoading,
	setThreadsList,
	threadsList,
	insightList,
	setInsightList,
	month,
	year
) => {
	setPostLoading(true);
	const token = getActiveToken();
	try {
		const baseUrl = "me/threads";

		const params = {
			fields:
				"id,text,timestamp,media_product_type,username,media_type,media_url,permalink",
			since: since,
			until: until,
			access_token: token,
			limit: 300,
			...(after && { after }),
		};

		const response = after
			? await graphGet(after)
			: await graphGet(baseUrl, { params });
		const newThreads = response.data.data || [];
		const threadsWithInsights = await Promise.all(
			newThreads.map(async (thread) => {
				if (thread.media_type !== "REPOST_FACADE") {
					const insights = await getThreadInsightsLegacy(thread.id);
					return { ...thread, ...insights };
				} else {
					return { ...thread, views: 0, likes: 0, replies: 0, reposts: 0 };
				}
			})
		);

		const filteredThreads = threadsWithInsights.filter((thread) => thread.text);

		const weeklyList = createWeeklyListLegacy(filteredThreads);
		// Create a new list with ranks added to each post
		const rankedWeeklyList = weeklyList.map((week) => {
			// Sort posts by views and add viewRank
			const viewSorted = [...week.posts].sort((a, b) => b.views - a.views);
			const viewRanked = viewSorted.map((post, index) => ({
				...post,
				viewRank: index + 1,
			}));

			// Sort posts by engagement score and add engagementRank
			const engagementSorted = [...viewRanked].sort((a, b) => {
				const scoreA = (a.likes * 1 + a.replies * 2) / a.views;
				const scoreB = (b.likes * 1 + b.replies * 2) / b.views;
				return scoreB - scoreA;
			});
			const engagementRanked = engagementSorted.map((post, index) => ({
				...post,
				engagementRank: index + 1,
			}));

			// Sort by timestamp in descending order (newest first)
			const timestampSorted = engagementRanked.sort(
				(a, b) => new Date(b.timestamp) - new Date(a.timestamp)
			);

			return {
				...week,
				posts: timestampSorted,
			};
		});

		const mothlyData = {
			month: month,
			year: year,
			week: rankedWeeklyList,
		};
		const previousThreads = threadsList;
		const newThreadsList = [...previousThreads, mothlyData];
		setThreadsList(newThreadsList);

		// Fetch insights data
		// use insight api call for merging profileViews to monthly threads object
		const insightsResponse = await getInsightPure(since, until);
		const newInsights = insightsResponse.data || [];

		// Combine previous insights with new insights
		// Sum up the values for each metric across all filtered threads
		const metricSums = filteredThreads.reduce(
			(sums, thread) => {
				sums.likes += thread.likes || 0;
				sums.replies += thread.replies || 0;
				sums.reposts += thread.reposts || 0;
				sums.views += thread.views || 0;
				return sums;
			},
			{ likes: 0, replies: 0, reposts: 0, views: 0 }
		);

		// Add the sums to the newInsights object
		newInsights.metricSums = metricSums;
		// Create monthlyView variable from newInsights.data[0].values
		const monthlyView = newInsights.data[0].values.map((item) => ({
			x: item.end_time,
			y: item.value,
		}));

		const updatedInsightList = [
			...(insightList || []),
			{
				month: month,
				year: year,
				profileViews: monthlyView,
				...metricSums,
			},
		];
		setInsightList(updatedInsightList);
	} catch (error) {
		console.error("Error fetching threads:", error);
		
		// Log error to Firestore (non-blocking)
		try {
			const userID = localStorage.getItem("userID");
			if (userID) {
				const errorLogRef = dbService.collection("errorLogs").doc(userID);
				await errorLogRef.update({
					errors: arrayUnion({
						type: "fetchMonthlyThreads",
						error: error.message,
						timestamp: new Date(),
					}),
				});
			}
		} catch (logError) {
			console.error("Failed to log error to Firestore:", logError);
		}
		
		throw error;
	} finally {
		setPostLoading(false);
	}
};

/**
 * Legacy: 라이프타임 인사이트 가져오기 (구버전 - 상태 설정 포함)
 * @deprecated - fetchLifetimeInsightsPure 사용 권장
 */
export const fetchLifetimeInsightsLegacy = async (
	since,
	until,
	setLifetimeInsightLoading
) => {
	setLifetimeInsightLoading(true);
	try {
		console.log("try fetchLifetimeInsights");
		const insightsResponse = await getInsightPure(since, until);
		const totalViews = insightsResponse.data.data[0].values.reduce(
			(sum, item) => sum + item.value,
			0
		);
		const profileViews = insightsResponse.data.data[0].values.map((item) => ({
			x: item.end_time,
			y: item.value,
		}));

		const filteredProfileViews = profileViews.reduce((acc, curr) => {
			if (acc.length === 0 && curr.y !== 0) {
				acc.push(curr);
			} else if (acc.length > 0) {
				acc.push(curr);
			}
			return acc;
		}, []);

		const lifetimeInsight = {
			profileViewsTotal: totalViews,
			profileViews: filteredProfileViews,
			followers: insightsResponse.data.data[1].total_value,
			likes: insightsResponse.data.data[2].total_value,
			replies: insightsResponse.data.data[3].total_value,
			reposts: insightsResponse.data.data[4].total_value,
		};
		return lifetimeInsight;
	} catch (error) {
		console.error("Error fetching lifetime insights:", error);

		try {
			const userID = localStorage.getItem("userID");
			const errorLogRef = dbService.collection("errorLogs").doc(userID);
			await errorLogRef.update({
				errors: arrayUnion({
					type: "fetchLifetimeInsights",
					error: error.message,
					timestamp: new Date(),
				}),
			});
		} catch (logError) {
			console.error("Failed to log error to Firestore:", logError);
		}

		throw error;
	} finally {
		setLifetimeInsightLoading(false);
	}
};

/**
 * Legacy: 라이프타임 스레드 데이터 가져오기 (구버전 - dispatch 기반)
 * @deprecated - fetchLifetimeThreadsOptimized 사용 권장
 */
export const fetchLifetimeThreadsLegacy = async (dispatch, userID) => {
	// ThreadsTypes import가 필요하지만 여기서는 레거시 보관용이므로 주석 처리
	// dispatch({ type: ThreadsTypes.SET_LOADING_STATE, payload: { key: 'threads', value: true } });
	
	const token = getActiveToken();
	let allThreads = [];
	let nextAfter;

	try {
		const userRef = dbService.collection("user").doc(userID);
		const userDoc = await userRef.get();

		const today = new Date();
		let since = 1688540400;
		let until = Math.floor(today.getTime() / 1000);

		if (userDoc.exists && userDoc.data().lastUpdated) {
			since = userDoc.data().lastUpdated;
			const threadsDoc = await userRef
				.collection("threads")
				.doc("threads")
				.get();

			if (threadsDoc.exists) {
				allThreads = threadsDoc.data().list || [];
			}
		}

		const baseUrl = "me/threads";
		do {
			const params = {
				fields:
					"id,text,timestamp,media_product_type,username,media_type,media_url,permalink",
				since: since,
				until: until,
				access_token: token,
				limit: 100,
				...(nextAfter && { after: nextAfter }),
			};

			const response = nextAfter
				? await graphGet(nextAfter)
				: await graphGet(baseUrl, { params });

			const newThreads = response.data.data || [];

			if (newThreads.length > 0) {
				const threadsWithInsights = await Promise.all(
					newThreads.map(async (thread) => {
						if (thread.media_type !== "REPOST_FACADE") {
							const insights = await getThreadInsightsLegacy(thread.id);
							return { ...thread, ...insights };
						} else {
							return { ...thread, views: 0, likes: 0, replies: 0, reposts: 0 };
						}
					})
				);

				const filteredThreads = threadsWithInsights.filter(
					(thread) => thread.text
				);

				if (filteredThreads.length > 0) {
					await userRef
						.collection("threads")
						.doc("threads")
						.set(
							{
								list: arrayUnion(...filteredThreads),
							},
							{ merge: true }
						);

					await userRef.update({
						lastUpdated: until,
					});

					allThreads = [...allThreads, ...filteredThreads];
				}
			}
			nextAfter = response.data.paging?.next || null;
		} while (nextAfter);

		// 아래 dispatch 호출들은 레거시 보관용이므로 주석 처리
		const allThreadsByDate = allThreads.sort((a, b) =>
			moment(b.timestamp).diff(moment(a.timestamp))
		);
		
		// dispatch({ type: ThreadsTypes.SET_THREADS, payload: allThreadsByDate });
		
		// ... 더 많은 레거시 로직들 (생략)
		
		return allThreadsByDate;
		
	} catch (error) {
		console.error("Error fetching lifetime threads:", error);
		if (allThreads.length > 0) {
			return allThreads;
		}
		throw error;
	}
};
